import ApiClient from '../../client';

// Namespaced `MyMeetings` — kept distinct from the legacy Whereby `meetings` concept that
// already exists on the Vue side (DISCOVERY §3.3). This module tracks AI-matching meetings
// across upcoming / pending / past tabs.

export const state = () => ({
  itemsByTab: { upcoming: [], pending: [], past: [] },
  currentTab: 'upcoming',
  loading: false,
  error: null
});

export const getters = {
  getItems: (state) => state.itemsByTab[state.currentTab] || [],
  getItemsByTab: (state) => (tab) => state.itemsByTab[tab] || [],
  isLoading: (state) => state.loading,
  failed: (state) => !!state.error,
  pendingCount: (state) => (state.itemsByTab.pending || []).length,
  upcomingCount: (state) => (state.itemsByTab.upcoming || []).length
};

export const mutations = {
  setTab(state, tab) {
    state.currentTab = tab;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setError(state, error) {
    state.error = error;
  },
  setItems(state, { tab, items }) {
    state.itemsByTab = { ...state.itemsByTab, [tab]: items || [] };
  },
  removeItem(state, meetingId) {
    Object.keys(state.itemsByTab).forEach((tab) => {
      state.itemsByTab[tab] = state.itemsByTab[tab].filter(
        (m) => m.id !== meetingId
      );
    });
  }
};

export const actions = {
  async fetchTab({ commit }, tab) {
    commit('setLoading', true);
    commit('setError', null);
    try {
      const response = await ApiClient.getMyMeetings(tab);
      commit('setItems', { tab, items: response.data?.items || [] });
    } catch (err) {
      commit('setError', err);
    } finally {
      commit('setLoading', false);
    }
  },
  async book({ dispatch }, payload) {
    const response = await ApiClient.bookMeeting(payload);
    await Promise.all([
      dispatch('fetchTab', 'pending'),
      dispatch('fetchTab', 'upcoming')
    ]);
    return response.data;
  },
  async cancelMeeting({ commit, dispatch }, { meetingId, reason, refreshTab }) {
    await ApiClient.cancelMyMeeting(meetingId, reason);
    commit('removeItem', meetingId);
    if (refreshTab) {
      await dispatch('fetchTab', refreshTab);
    }
  },
  // Accept a sponsor-initiated request: the meeting leaves the pending tab and becomes a
  // confirmed upcoming meeting, so refresh upcoming as well as the originating tab.
  async acceptMeeting({ commit, dispatch }, { meetingId, refreshTab }) {
    await ApiClient.acceptMyMeeting(meetingId);
    commit('removeItem', meetingId);
    await dispatch('fetchTab', 'upcoming');
    if (refreshTab && refreshTab !== 'upcoming') {
      await dispatch('fetchTab', refreshTab);
    }
  },
  async declineMeeting(
    { commit, dispatch },
    { meetingId, reason, refreshTab }
  ) {
    await ApiClient.declineMyMeeting(meetingId, reason);
    commit('removeItem', meetingId);
    if (refreshTab) {
      await dispatch('fetchTab', refreshTab);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
